/**
 * Progressive chunk uploader — uploads 10s audio chunks to S3
 * as they are recorded, rather than waiting until recording stops.
 *
 * When VITE_USE_TUS is enabled, chunks are uploaded via tus-js-client
 * for built-in resumability. Otherwise falls back to presign → S3 PUT.
 *
 * On recording stop: finalize-session → server-side FFmpeg stitching.
 */

import { getPresignedChunkUrl, finalizeChunkSession } from '../api'
import type { FinalizeSessionResponse } from '../api'
import { updateRecordingChunkMeta } from '../db/chunks'
import { isOnline } from '../offline'
import { uploadBlobWithRetry } from './uploader'
import { uploadChunkViaTus } from './tusUploader'

const useTus = import.meta.env.VITE_USE_TUS === 'true'

interface ChunkUploadTask {
  sessionId: string
  chunkIndex: number
  blob: Blob
  mimeType: string
  totalChunks: number
}

interface FinalizeMetadata {
  submissionId: string
  totalChunks: number
  assetId: string
  indicationId: string
  kiqIds: string[]
  insightTags: string[]
  createdAt: string
  audioMimeType?: string
  durationMs?: number
  participants?: unknown[]
  recordingWithoutParticipants?: boolean
  consentDescription?: string
  consentConfirmed?: boolean
}

interface ChunkProgress {
  uploaded: number
  total: number
  pending: number
}

// Per-session upload queues to ensure sequential upload
const sessionQueues = new Map<string, Promise<void>>()
const sessionProgress = new Map<string, ChunkProgress>()

function getProgress(sessionId: string): ChunkProgress {
  return sessionProgress.get(sessionId) ?? { uploaded: 0, total: 0, pending: 0 }
}

function updateProgress(
  sessionId: string,
  update: Partial<ChunkProgress>,
): void {
  const current = getProgress(sessionId)
  sessionProgress.set(sessionId, { ...current, ...update })
}

async function doUploadChunkViaTus(task: ChunkUploadTask, chunkDbId: string, resumeUrl?: string | null): Promise<boolean> {
  const result = await uploadChunkViaTus({
    sessionId: task.sessionId,
    chunkIndex: task.chunkIndex,
    blob: task.blob,
    mimeType: task.mimeType,
    totalChunks: task.totalChunks,
    resumeUrl,
  })

  await updateRecordingChunkMeta(chunkDbId, {
    uploadStatus: 'uploaded',
    uploadedAt: new Date().toISOString(),
    lastUploadAttemptAt: new Date().toISOString(),
    tusUploadUrl: result.uploadUrl,
  })

  return true
}

async function doUploadChunkViaPresign(task: ChunkUploadTask, chunkDbId: string): Promise<boolean> {
  const { presignedUrl } = await getPresignedChunkUrl(
    task.sessionId,
    task.chunkIndex,
    task.totalChunks,
    task.mimeType,
  )

  await uploadBlobWithRetry(presignedUrl, task.blob)

  await updateRecordingChunkMeta(chunkDbId, {
    uploadStatus: 'uploaded',
    uploadedAt: new Date().toISOString(),
    lastUploadAttemptAt: new Date().toISOString(),
  })

  return true
}

async function doUploadChunk(task: ChunkUploadTask, chunkDbId: string, resumeUrl?: string | null): Promise<boolean> {
  if (!isOnline()) {
    return false
  }

  try {
    const success = useTus
      ? await doUploadChunkViaTus(task, chunkDbId, resumeUrl)
      : await doUploadChunkViaPresign(task, chunkDbId)

    if (success) {
      const progress = getProgress(task.sessionId)
      updateProgress(task.sessionId, {
        uploaded: progress.uploaded + 1,
        pending: Math.max(0, progress.pending - 1),
      })
    }

    return success
  } catch (error) {
    console.warn(
      `[chunkUploader] Failed to upload chunk ${task.chunkIndex} for session ${task.sessionId}:`,
      error,
    )

    const tusUrl = useTus && typeof error === 'object' && error !== null && 'uploadUrl' in error
      ? (error as { uploadUrl: string | null }).uploadUrl
      : null

    await updateRecordingChunkMeta(chunkDbId, {
      uploadStatus: 'failed',
      lastUploadAttemptAt: new Date().toISOString(),
      lastUploadError: error instanceof Error ? error.message : String(error),
      retryCount: ((await getRetryCount(chunkDbId)) ?? 0) + 1,
      ...(tusUrl ? { tusUploadUrl: tusUrl } : {}),
    })

    return false
  }
}

async function getRetryCount(chunkDbId: string): Promise<number> {
  try {
    const { db } = await import('../db/database')
    const chunk = await db.chunks.get(chunkDbId)
    return chunk?.retryCount ?? 0
  } catch {
    return 0
  }
}

/**
 * Queue a chunk for progressive upload. Chunks for the same session
 * are uploaded sequentially (in order), but upload is non-blocking
 * for the recording flow.
 */
export function uploadChunk(
  sessionId: string,
  chunkIndex: number,
  blob: Blob,
  mimeType: string,
  totalChunks: number,
  chunkDbId: string,
  resumeUrl?: string | null,
): void {
  const task: ChunkUploadTask = {
    sessionId,
    chunkIndex,
    blob,
    mimeType,
    totalChunks,
  }

  const progress = getProgress(sessionId)
  updateProgress(sessionId, {
    total: Math.max(progress.total, chunkIndex + 1),
    pending: progress.pending + 1,
  })

  // Chain onto the session's sequential queue
  const previousPromise = sessionQueues.get(sessionId) ?? Promise.resolve()
  const nextPromise = previousPromise.then(() => {
    return doUploadChunk(task, chunkDbId, resumeUrl).then(() => undefined)
  })
  sessionQueues.set(sessionId, nextPromise)
}

/**
 * Finalize a chunk session after recording stops.
 * Tells the backend all chunks have been uploaded and triggers server-side stitching.
 */
export async function finalizeSession(
  sessionId: string,
  metadata: FinalizeMetadata,
): Promise<FinalizeSessionResponse> {
  // Wait for any in-flight chunk uploads to complete
  const pending = sessionQueues.get(sessionId)
  if (pending) {
    await pending
  }

  const result = await finalizeChunkSession(
    sessionId,
    metadata.submissionId,
    {
      totalChunks: metadata.totalChunks,
      assetId: metadata.assetId,
      indicationId: metadata.indicationId,
      kiqIds: metadata.kiqIds,
      insightTags: metadata.insightTags,
      createdAt: metadata.createdAt,
      audioMimeType: metadata.audioMimeType,
      durationMs: metadata.durationMs,
      participants: metadata.participants,
      recordingWithoutParticipants: metadata.recordingWithoutParticipants,
      consentDescription: metadata.consentDescription,
      consentConfirmed: metadata.consentConfirmed,
    },
  )

  // Cleanup session state
  sessionQueues.delete(sessionId)
  sessionProgress.delete(sessionId)

  return result
}

/**
 * Get the upload progress for a session.
 */
export function getSessionProgress(sessionId: string): ChunkProgress {
  return getProgress(sessionId)
}

/**
 * Clean up all state for a session (e.g., on unmount).
 */
export function destroySession(sessionId: string): void {
  sessionQueues.delete(sessionId)
  sessionProgress.delete(sessionId)
}
